import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import FilingDetails from "../../pages/private/create-filing/FilingDetails";
import TaxSituation from "../../pages/private/create-filing/TaxSituation";
import TaxPrice from "../../pages/private/create-filing/TaxPrice";
import {
  fetchCurrentFiling,
  getFilingServices,
} from "../../api/filings/filingAPI";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";
import { getInitialStep } from "../../utils/TaxFilingCreateWizard/stepInitializer";
import ErrorHandler from "../../pages/errors/ErrorHandler";
import { Handler401 } from "../../api/utils/responseActions";
import EmptyState from "../ui/pages/EmptyState";
import OnboardingStep from "../navegation/OnboardingStep";
import { useRefreshOnAuth } from "../../hooks/useRefreshOnAuth";

function TaxFilingCreateWizard() {
  const [filingData, setFilingData] = useState({});
  const [allowServiceData, setAllowServiceData] = useState(null);
  const [step, setStep] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const { filingUuid } = useParams();
  const [searchParams] = useSearchParams();
  const isDemoFromUrl = searchParams.get("mode") === "DE";

  const isNewFiling = !filingUuid;

  const fetchAllowedFilingServices = useCallback(async () => {
    const allowedServicesUrl = `tax/tax-services${
      isDemoFromUrl ? "?mode=DE" : ""
    }`;
    try {
      // Get the list of allowed tax services for the authenticated user.
      const response = await getFilingServices(allowedServicesUrl);
      const allowServiceData = await response.json();

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      setAllowServiceData(allowServiceData.services);
      setLoading(false);
    } catch (err) {
      console.log(err.message);
    }
  }, [isDemoFromUrl, navigate]);

  const fetchFilingData = useCallback(async () => {
    try {
      const filingDataUrl = `tax/filing`;

      // First fetch the filing data to get mode status
      const filingResponse = await fetchCurrentFiling(
        filingDataUrl,
        filingUuid,
      );

      if (!filingResponse.ok) {
        if (filingResponse.status === 401) {
          Handler401(navigate);
          return;
        }
        const error = new Error("Failed to fetch filing data");
        throw error;
      }

      const filingData = await filingResponse.json();

      // Determine if this is a demo filing (from filing data OR URL param)
      const isDemo = filingData.mode === "DE" || isDemoFromUrl;
      const allowedServicesUrl = `tax/tax-services${isDemo ? "?mode=DE" : ""}`;

      // Then fetch services with the correct demo flag
      const allowServiceResponse = await getFilingServices(allowedServicesUrl);

      if (!allowServiceResponse.ok) {
        if (allowServiceResponse.status === 401) {
          Handler401(navigate);
          return;
        }
        const error = new Error("Failed to fetch allowed services");
        throw error;
      }

      const allowServiceData = await allowServiceResponse.json();

      setFilingData(filingData);
      setAllowServiceData(allowServiceData.services);

      // If filing status indicates payment is complete, redirect to dashboard
      if (
        filingData.status === "ACT" ||
        filingData.status === "PRO" ||
        filingData.status === "COM"
      ) {
        navigate(`/tax-filing/filing/${filingData.filing_uuid}`);
        return;
      }

      const initialStep = getInitialStep(filingData);
      setStep(initialStep);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setStatus(err.status || 500);
      setLoading(false);
    }
  }, [filingUuid, navigate, isDemoFromUrl]);

  const handleRefresh = useCallback(() => {
    if (!isNewFiling) {
      fetchFilingData();
    } else {
      fetchAllowedFilingServices();
    }
  }, [isNewFiling, fetchFilingData, fetchAllowedFilingServices]);

  // This side effect is used for: (a) Refresh page (b) When render the page for first
  useRefreshOnAuth(handleRefresh);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  // Refetch filing data when step changes to ensure we have latest data (including addresses)
  // Only refetch, don't reset the step
  useEffect(() => {
    if (!isNewFiling && filingUuid && step > 0) {
      // Only refetch data, don't reset step
      const refetchData = async () => {
        try {
          const filingDataUrl = `tax/filing`;
          const filingResponse = await fetchCurrentFiling(
            filingDataUrl,
            filingUuid,
          );
          if (filingResponse.ok) {
            const filingData = await filingResponse.json();
            setFilingData(filingData);
          }
        } catch (err) {
          // Silently fail - don't block user flow
          console.error("Error refetching filing data:", err);
        }
      };
      refetchData();
    }
  }, [step, isNewFiling, filingUuid]);

  const steps = [
    { component: FilingDetails, label: "tax-type" },
    {
      component: TaxSituation,
      label: "tax-situation",
      schemaKey: "Tax_Basics",
    },
    {
      component: TaxSituation,
      label: "tax-background",
      schemaKey: "Background",
    },
    { component: TaxPrice, label: "tax-price" },
  ];

  const CurrentStep = steps[step].component;

  const nextStep = (data) => {
    setFilingData((prevData) => {
      const newData = { ...prevData, ...data };
      return newData;
    });

    if (step < steps.length - 1) {
      setStep((prevStep) => prevStep + 1);
    } else {
      // Navigate to a success page or dashboard
      navigate("/tax-filing");
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  // Check if services array is empty (only when data has loaded)
  const isServicesEmpty =
    !loading &&
    !error &&
    allowServiceData &&
    Array.isArray(allowServiceData) &&
    allowServiceData.length === 0;

  return (
    <div className="main-content-area">
      <div className="content">
        {loading && <LoadingPlaceholder rows={1} type="card-large" />}
        {error && <ErrorHandler status={status} errorMessage={error} />}
        {!loading && !error && isServicesEmpty && (
          <EmptyState
            title="Services Temporarily Unavailable"
            text="Our filing services are temporarily closed. Please check back later."
          />
        )}
        {!loading && !error && !isServicesEmpty && (
          <>
            <div className="internal-content-header">
              <OnboardingStep
                steps={[
                  { number: 1, text: "Tax Type" },
                  { number: 2, text: "Tax Situation" },
                  { number: 3, text: "Background Info" },
                  { number: 4, text: "Your Price" },
                ]}
                currentStep={step}
                /*title="New Filing"*/
              />
            </div>
            <div className="internal-content-body">
              <CurrentStep
                filingData={filingData}
                allowServiceData={allowServiceData}
                onNext={nextStep}
                onPrev={prevStep}
                isNewFiling={isNewFiling}
                {...(steps[step].schemaKey && {
                  schemaKey: steps[step].schemaKey,
                })}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TaxFilingCreateWizard;
